// src/api/bookings.ts
import api from "./client";

export type BookingStatus = "confirmed" | "cancelled";
export type CalendarProvider = "google" | "microsoft" | null;

export type PaymentStatus =
  | "checkout_created"
  | "paid"
  | "partially_refunded"
  | "refunded"
  | "expired"
  | "failed"
  | "disputed"
  | "anomaly_paid_after_cancellation";

export interface Booking {
  booking_id: string;
  service_name: string;
  duration_minutes: number;
  preferred_date_time: string;
  notes: string;
  customer_name: string;
  customer_email: string;
  customer_phone: string;
  status: BookingStatus;
  provider: CalendarProvider;
  provider_calendar_id: string | null;
  calendar_event_id: string | null;
  calendar_sync_available: boolean;
  // Payment fields (present when business has payments configured)
  payment_required: boolean;
  payment_status: PaymentStatus | null;
  amount_due_minor_units: number | null;
  balance_due_minor_units: number | null;
  platform_fee_minor_units: number | null;
  payment_purpose: "deposit" | "full_payment" | null;
  paid_at: string | null;
  stripe_checkout_session_id: string | null;
  refund_amount_minor_units: number | null;
  refund_date: string | null;
  currency: string | null;
}

export interface BookingsResponse {
  bookings: Booking[];
  total: number;
  page: number;
  page_size: number;
}

export interface BookingsFiltersParams {
  page?: number;
  page_size?: number;
  date_from?: string;
  date_to?: string;
  service?: string;
  status?: string;
  payment_status?: string;
}

export interface CancelBookingResponse {
  booking_id: string;
  status: "cancelled";
  calendar_synced: boolean;
  calendar_sync_error: string | null;
}

export interface RescheduleBookingResponse {
  booking_id: string;
  preferred_date_time: string;
  calendar_synced: boolean;
  calendar_sync_error: string | null;
}

export interface UpdateBookingResponse {
  booking_id: string;
  service_name: string;
  notes: string;
  calendar_synced: boolean;
  calendar_sync_error: string | null;
}

export interface RetrySyncResponse {
  calendar_synced: boolean;
  calendar_sync_error: string | null;
}

export const fetchBookings = async (
  filters: BookingsFiltersParams = {}
): Promise<BookingsResponse> => {
  const params: Record<string, string | number> = {
    page: filters.page ?? 1,
    page_size: filters.page_size ?? 10,
  };

  if (filters.date_from) params.date_from = filters.date_from;
  if (filters.date_to) params.date_to = filters.date_to;
  if (filters.service) params.service = filters.service;
  if (filters.status) params.status = filters.status;
  if (filters.payment_status) params.payment_status = filters.payment_status;

  const response = await api.get<BookingsResponse>("/getBookings", { params });
  return response.data;
};

export const cancelBooking = async (
  bookingId: string
): Promise<CancelBookingResponse> => {
  const response = await api.post<CancelBookingResponse>("/cancelBooking", {
    booking_id: bookingId,
  });
  return response.data;
};

export const rescheduleBooking = async (
  bookingId: string,
  newDatetime: string
): Promise<RescheduleBookingResponse> => {
  const response = await api.post<RescheduleBookingResponse>(
    "/rescheduleBooking",
    { booking_id: bookingId, new_datetime: newDatetime }
  );
  return response.data;
};

export const updateBooking = async (
  bookingId: string,
  payload: { service_name?: string; notes?: string }
): Promise<UpdateBookingResponse> => {
  const response = await api.post<UpdateBookingResponse>("/updateBooking", {
    booking_id: bookingId,
    ...payload,
  });
  return response.data;
};

export const retrySyncBooking = async (
  bookingId: string,
  action: "cancel" | "reschedule" | "update_title",
  extra?: { newDatetime?: string; newTitle?: string }
): Promise<RetrySyncResponse> => {
  const response = await api.post<RetrySyncResponse>("/retrySyncBooking", {
    booking_id: bookingId,
    action,
    new_datetime: extra?.newDatetime ?? null,
    new_title: extra?.newTitle ?? null,
  });
  return response.data;
};
